import React, { Suspense, useState, useEffect, useRef } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { addNotification, clearNotifications } from './common/state/notifications/reducer'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { RootState } from './common/reducers'
import { FireLoading } from './components/loading'
import { MiningLoading } from './components/mining-loading'
import { io } from 'socket.io-client'
import { startPresence, stopPresence } from './socket/presenceSocket'

const Providers = React.lazy(() => import('./providers'))
import './App.css'
import { useClickSound } from './hooks/useClickSound'
import { loadSettings } from './common/state/settings/reducer'
import { stopPVEAudio } from './hooks/usePVEAudio'
import { clearAuthToken } from './utils/authToken'
import {
  useWeb3Context,
  setGameSessionWalletAddress,
  hasPersistedEmailSessionWallet,
} from './contexts/web3Context'
import config from './utils/config'
import GlobalAttackAlert from './components/GlobalAttackAlert'
import { FogTransitionProvider } from './game/world-map/FogTransitionContext'
import { WORLD_MAP_FOG_BACKDROP } from './game/world-map/FogTransitionContext'
import { DayNightProvider } from './contexts/DayNightContext'
import { KingdomMusicPlayer } from './components/KingdomMusicPlayer'
import { PvPMusicPlayer } from './components/PvPMusicPlayer'
import { StartMusicPlayer } from './components/StartMusicPlayer'
import { MiniGameMusicPlayer } from './components/MiniGameMusicPlayer'
import { LevelUpTracker } from './components/LevelUpTracker'
import { getProfile, clearGameAuthSession } from './common/api'
import { getResources } from './store/user/actions'
import { fetchWeapons } from './common/state/character/action'
import { ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT } from './common/allianceApi'
import { refreshAccessToken } from './utils/axiosSessionBootstrap'
import { initializeReferralCode } from './utils/referralUtils'
import {
  setSessionExpiredHandler,
  hasSessionExpiresAt,
  isSessionExpiredByTimestamp,
  hasPendingReauthAfterSessionExpiry,
  isGameRootEntryAllowed,
  clearEclipseraSessionStorageKeys,
  hasEstablishedGameSession,
  clearPendingReauthAfterSessionExpiry,
  clearSessionExpiresAt,
} from './utils/sessionExpiration'
import SessionExpiredModal from './components/Modal/SessionExpiredModal'
import { VercelAssetBootstrap } from './components/VercelAssetBootstrap'
import { RouteAssetGateByPath } from './components/RouteAssetGate'
import { AlertHost, alert } from './components/Alert/Alert'
import { DepositAlertHost } from './components/Alert/DepositAlert'
import { EcsPackAlertHost } from './components/Alert/EcsPackAlert'
import CursorClickEffect from './components/CursorClickEffect/CursorClickEffect'
import ResourceClaimFlyLayer, { ResourceGainWatcher } from './components/Main/ResourceClaimFlyLayer'
import ResourceSpendFlyLayer, { ResourceSpendWatcher } from './components/Main/ResourceSpendFlyLayer'
import { AllianceTaskWatcher } from './components/alliances/AllianceTaskWatcher'
import { AllianceJoinRequestWatcher } from './components/alliances/AllianceJoinRequestWatcher'
import { AllianceConquerRequestWatcher } from './components/alliances/AllianceConquerRequestWatcher'
import { CastleConquerResultWatcher } from './components/alliances/CastleConquerResultWatcher'
import { AddEmailPromptBanner } from './components/EmailAuth/AddEmailPromptBanner'
import { AutoLinkVirtualSolanaWallet } from './components/Header/AutoLinkVirtualSolanaWallet'
import { GlobalPvPRematchHost } from './components/PvPBattle/GlobalPvPRematchHost'
import { AllianceLeadershipChallengeGlobalHost } from './components/alliances/AllianceLeadershipChallengeGlobalHost'
import { GlobalDirectChallengeHost } from './components/PvPBattle/GlobalDirectChallengeHost'
import { GlobalFriendRequestHost } from './components/PvPBattle/GlobalFriendRequestHost'
import { AppViewportProvider } from './contexts/appViewportContext'
import {
  MissionObjectiveCompleteNotification,
  MissionQuestCompleteNotification,
  MissionCompleteNotification,
  GlobalNotificationToastHost,
} from './components/notifications'
import AllianceJoinNudgeHost from './components/alliances/AllianceJoinNudgeHost'
import GlobalTooltipHost from './components/tooltip/GlobalTooltipHost'

import store from './store'
import { FtueSkipButton } from './components/ftue/FtueSkipButton'

const DefaultLayout = React.lazy(() =>
  import('./pages/default-layout').then((module) => ({
    default: module.DefaultLayout,
  })),
)
const Main = React.lazy(() =>
  import('./pages').then((module) => ({ default: module.Main })),
)
const GamePage = React.lazy(() =>
  import('./pages').then((module) => ({ default: module.GamePage })),
)
const ShopPage = React.lazy(() =>
  import('./pages/shop-page').then((module) => ({
    default: module.ShopPage,
  })))
const MiningFactory = React.lazy(() =>
  import('./pages/mining-factory').then((module) => ({
    default: module.MiningFactory,
  })))
const MiniGamePage = React.lazy(() => import('./pages/mini-game-page'))
const CharacterSelectPage = React.lazy(() =>
  import('./pages/character-select-page').then((module) => ({
    default: module.CharacterSelectPage,
  })))
const CharacterDetailsPage = React.lazy(() =>
  import('./pages/character-details-page').then((module) => ({
    default: module.CharacterDetailsPage,
  })))

const InventoryPage = React.lazy(() =>
  import('./pages/inventory-page').then((module) => ({
    default: module.InventoryPage,
  })))

const PVERound = React.lazy(() =>
  import('./pages/section-page').then((module) => ({
    default: module.PVERound,
  })))

const BattlefieldPage = React.lazy(() =>
  import('./pages/battlefield-page').then((module) => ({
    default: module.BattlefieldPage,
  })))
const BattlePreviewPage = React.lazy(() =>
  import('./pages/battle-preview-page').then((module) => ({
    default: module.BattlePreviewPage,
  })))

const BankPage = React.lazy(() =>
  import('./pages/bank-page').then((module) => ({
    default: module.BankPage,
  })))

const SupportPage = React.lazy(() =>
  import('./pages/support-page').then((module) => ({
    default: module.SupportPage,
  })))

const EspionagePage = React.lazy(() =>
  import('./pages/espionage-page').then((module) => ({
    default: module.EspionagePage,
  })))

const EspionagePageV2 = React.lazy(() =>
  import('./components/Espionage/EspionagePageV2').then((module) => ({
    default: module.EspionagePageV2,
  })))

const SpySplashPage = React.lazy(() =>
  import('./pages/spy-splash-page').then((module) => ({
    default: module.SpySplashPage,
  })))

const AlliancePage = React.lazy(() =>
  import('./pages/alliance-page').then((module) => ({
    default: module.AlliancePage,
  })))
const AllianceManagePage = React.lazy(() =>
  import('./pages/alliance-manage-page').then((module) => ({
    default: module.AllianceManagePage,
  })))

const TowerPage = React.lazy(() =>
  import('./pages/tower-page').then((module) => ({
    default: module.TowerPage,
  })))

const HospitalPage = React.lazy(() =>
  import('./pages/hospital-page').then((module) => ({
    default: module.HospitalPage,
  })))

const WoodHousePage = React.lazy(() =>
  import('./pages/woodhouse-page').then((module) => ({
    default: module.WoodHousePage,
  })))

const WheatFarmPage = React.lazy(() =>
  import('./pages/wheatfarm-page').then((module) => ({
    default: module.WheatFarmPage,
  })))

const StoneFactoryPage = React.lazy(() =>
  import('./pages/stonefactory-page').then((module) => ({
    default: module.StoneFactoryPage,
  })))

const TrainingPage = React.lazy(() =>
  import('./pages/training-page').then((module) => ({
    default: module.TrainingPage,
  })))

const TournamentPage = React.lazy(() =>
  import('./pages/pvp/tournament-page').then((module) => ({
    default: module.TournamentPage,
  })))

const TournamentBattlePage = React.lazy(() =>
  import('./pages/pvp/tournament-battle-page').then((module) => ({
    default: module.TournamentBattlePage,
  })))

const HuntingLodgePage = React.lazy(() =>
  import('./pages/hunting-lodge-page').then((module) => ({
    default: module.HuntingLodgePage,
  })))

const IronMinePage = React.lazy(() =>
  import('./pages/iron-mine-page').then((module) => ({
    default: module.IronMinePage,
  })))

const BakeryPage = React.lazy(() =>
  import('./pages/bakery-page').then((module) => ({
    default: module.BakeryPage,
  })))

const WellPage = React.lazy(() =>
  import('./pages/well-page').then((module) => ({
    default: module.WellPage,
  })))

const BlacksmithPage = React.lazy(() =>
  import('./pages/blacksmith-page').then((module) => ({
    default: module.BlacksmithPage,
  })))

const WallPage = React.lazy(() =>
  import('./pages/wall-page').then((module) => ({
    default: module.WallPage,
  })))

const PvPPage = React.lazy(() =>
  import('./pages/pvp/pvp-page').then((module) => ({
    default: module.PvPPage,
  })))
const PvpLobbyPage = React.lazy(() =>
  import('./pages/pvp/pvpLobby').then((module) => ({
    default: module.PvpLobbyPage,
  })))
const PvPDirectChallengePage = React.lazy(() =>
  import('./pages/pvp/pvp-direct-challenge-page').then((module) => ({
    default: module.PvPDirectChallengePage,
  })))
const PvPDirectChallengeAcceptedPage = React.lazy(() =>
  import('./pages/pvp/pvp-direct-challenge-accepted-page').then((module) => ({
    default: module.PvPDirectChallengeAcceptedPage,
  })))
const PvPCharacterSelectSimplePage = React.lazy(() =>
  import('./pages/pvp/pvp-character-select-simple-page').then((module) => ({
    default: module.PvPCharacterSelectSimplePage,
  })))
const PvPWaitingSimplePage = React.lazy(() =>
  import('./pages/pvp/pvp-waiting-simple-page').then((module) => ({
    default: module.PvPWaitingSimplePage,
  })))
const PvPBattlePage = React.lazy(() =>
  import('./pages/pvp/pvp-battle-page').then((module) => ({
    default: module.PvPBattlePage,
  })))
const PvPAllianceLeadershipRoomPage = React.lazy(() =>
  import('./pages/pvp-alliance-leadership-room-page').then((module) => ({
    default: module.PvPAllianceLeadershipRoomPage,
  })))
const PvPObserverBattlePage = React.lazy(() =>
  import('./pages/pvp/pvp-observer-battle-page').then((module) => ({
    default: module.PvPObserverBattlePage,
  })))

const LeaderboardPage = React.lazy(() =>
  import('./pages/leaderboard-page').then((module) => ({
    default: module.LeaderboardPage,
  })))

const MapPage = React.lazy(() =>
  import('./pages/map-page').then((module) => ({
    default: module.MapPage,
  })))

const Castle1 = React.lazy(() =>
  import('./pages/Castle/Castle1').then((module) => ({
    default: module.default,
  })))

const Castle2 = React.lazy(() =>
  import('./pages/Castle/Castle2').then((module) => ({
    default: module.default,
  })))

const Castle3 = React.lazy(() =>
  import('./pages/Castle/Castle3').then((module) => ({
    default: module.default,
  })))

const Castle4 = React.lazy(() =>
  import('./pages/Castle/Castle4').then((module) => ({
    default: module.default,
  })))

const Castle5 = React.lazy(() =>
  import('./pages/Castle/Castle5').then((module) => ({
    default: module.default,
  })))

const StartPage = React.lazy(() =>
  import('./pages/StartPage').then((module) => ({
    default: module.default,
  })))

const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'))

const DefaultCharacterSelectPage = React.lazy(() =>
  import('./pages/character-select-page').then((module) => ({
    default: module.default,
  })))

const MainCharacterSelectPage = React.lazy(() =>
  import('./pages/main-character-select-page').then((module) => ({
    default: module.MainCharacterSelectPage,
  })))

const KingdomMainPage = React.lazy(() =>
  import('./pages/kingdom/kingdom-main-page').then((module) => ({
    default: module.KingdomMainPage,
  })))

const GeneralAppointmentPage = React.lazy(() =>
  import('./pages/kingdom/general-appointment-page').then((module) => ({
    default: module.GeneralAppointmentPage,
  })))

const GeneralVisitPage = React.lazy(() =>
  import('./pages/kingdom/general-visit-page').then((module) => ({
    default: module.GeneralVisitPage,
  })))

const RetirementPage = React.lazy(() =>
  import('./pages/kingdom/retirement-page').then((module) => ({
    default: module.RetirementPage,
  })))

const DiplomacyPage = React.lazy(() =>
  import('./pages/kingdom/diplomacy-page').then((module) => ({
    default: module.DiplomacyPage,
  })))

const TechnologyResearchPage = React.lazy(() =>
  import('./pages/kingdom/technology-research-page').then((module) => ({
    default: module.TechnologyResearchPage,
  })))

const KingdomMvmCaptivesPage = React.lazy(() =>
  import('./pages/kingdom/kingdom-mvm-captives-page').then((module) => ({
    default: module.KingdomMvmCaptivesPage,
  })))

const NotFoundPage = React.lazy(() =>
  import('./pages/error-pages').then((module) => ({
    default: module.NotFoundPage,
  })))

const ServerErrorPage = React.lazy(() =>
  import('./pages/error-pages').then((module) => ({
    default: module.ServerErrorPage,
  })))

const NetworkErrorPage = React.lazy(() =>
  import('./pages/error-pages').then((module) => ({
    default: module.NetworkErrorPage,
  })))

/** Retry lazy import on ChunkLoadError (common with HMR/cache) */
function lazyWithRetry(importFn: () => Promise<any>, retries = 3) {
  return () =>
    importFn().catch((err) => {
      if (retries > 0 && (err?.name === 'ChunkLoadError' || err?.message?.includes('Loading chunk'))) {
        return new Promise((resolve) => {
          setTimeout(() => resolve(lazyWithRetry(importFn, retries - 1)()), 1000)
        })
      }
      throw err
    })
}

const HexBattlePage = React.lazy(
  lazyWithRetry(() =>
    import(/* webpackChunkName: "hex-battle" */ /* webpackPrefetch: true */ './pages/hex-battle-page').then((module) => ({
      default: module.HexBattlePage,
    })),
  ),
)

/** Matches in-page shell so Suspense boundary does not CLS when the chunk attaches. */
function WorldMapRouteFallback() {
  return (
    <div
      role="progressbar"
      aria-busy
      aria-label="Loading world map"
      style={{
        position: 'fixed',
        inset: 0,
        minHeight: '100dvh',
        background: WORLD_MAP_FOG_BACKDROP,
        zIndex: 1,
      }}
    />
  )
}

const WorldMapPage = React.lazy(
  lazyWithRetry(() =>
    import(/* webpackChunkName: "world-map" */ /* webpackPrefetch: true */ './game/world-map/WorldMapPage').then((module) => ({
      default: module.WorldMapPage,
    })),
  ),
)


/**
 * Keeps the main land page (DefaultLayout → GamePage → Main) alive across all
 * route changes so that Main.tsx never unmounts when the player navigates to
 * /shop, /castle1, /hex-battle, etc.
 *
 * HOW IT WORKS
 * ────────────
 * Instead of going through <Routes> (which mounts/unmounts the matched element),
 * this component renders once and then hides itself with CSS when the active
 * route is not "/".  React keeps the component tree intact in the DOM, so:
 *   • socket connections in useSocket stay open
 *   • Redux subscriptions and building cooldown timers keep ticking
 *   • the 8-second mining-status interval runs uninterrupted
 *   • all useState values (ecs, water, resources…) are preserved
 *   • navigating *back* to "/" shows the land page instantly with 0 reloads
 *
 * FIRST-VISIT LAZY MOUNT
 * ──────────────────────
 * The component intentionally does not render at all until the user first
 * visits "/".  If the user deep-links to /shop, we do not bootstrap Main.tsx
 * in the background — that would waste API quota.  Once "/" has been visited
 * once, the component stays mounted forever for the session lifetime.
 *
 * INTERACTIVITY WHILE HIDDEN
 * ──────────────────────────
 * `pointer-events: none` + `aria-hidden` prevent any accidental interaction
 * with the hidden land page while the user is on another route.  The `inert`
 * attribute (supported in all modern browsers) additionally prevents keyboard
 * focus traversal into the hidden tree.
 */
function PersistentLandPage({
  showAccount,
  openModal,
}: {
  showAccount: boolean
  openModal: (flag: boolean) => void
}) {
  const location = useLocation()
  const isLandRoute = location.pathname === '/'

  // Only mount after the first visit to "/" — avoid bootstrapping the heavy
  // land page if the user entered via a different route (e.g. /shop direct link).
  const [hasMounted, setHasMounted] = React.useState(isLandRoute)
  React.useEffect(() => {
    if (isLandRoute && !hasMounted) setHasMounted(true)
  }, [isLandRoute, hasMounted])

  if (!hasMounted) return null

  return (
    // When isLandRoute is false we hide but keep mounted (the keep-alive behaviour).
    // `inert` makes the entire hidden subtree inert to keyboard/pointer/AT.
    <div
      style={{
        display: isLandRoute ? undefined : 'none',
        pointerEvents: isLandRoute ? undefined : 'none',
      }}
      aria-hidden={isLandRoute ? undefined : true}
      // @ts-ignore — `inert` is a valid HTML attribute in modern browsers
      inert={isLandRoute ? undefined : ''}
    >
      <Suspense fallback={<FireLoading />}>
        <DefaultLayout
          onModalShow={openModal}
          component={
            <Suspense fallback={<FireLoading />}>
              <GamePage
                showAccount={showAccount}
                setShowAccount={openModal}
              />
            </Suspense>
          }
        />
      </Suspense>
    </div>
  )
}

// Component to handle route changes and stop PVE audio when leaving PVE pages
const RouteChangeListener: React.FC = () => {
  const location = useLocation()

  useEffect(() => {
    const isPVERoute = location.pathname.startsWith('/pve/')

    if (!isPVERoute) {
      // User navigated away from PVE pages, stop the music
      stopPVEAudio()
    }
  }, [location.pathname])

  return null
}

// Component to handle user online/offline status via socket
// Socket is deferred on `/start` so the gate screen does not open land sockets or "online" pings.
const UserOnlineStatusManager: React.FC = () => {
  const { address } = useWeb3Context()
  const location = useLocation()
  const dispatch = useDispatch()
  const socketRef = useRef<any>(null)
  const deferLandSocket = location.pathname === '/start'
  const leadershipHudRefreshAtRef = useRef(0)

  // Heartbeat presence: ONE always-on authenticated connection per wallet, on EVERY route including
  // `/start` (it carries presence only — no land subscriptions). Lives outside React's tree, so route
  // changes never interrupt the heartbeat; torn down only when the wallet clears/changes.
  useEffect(() => {
    if (!address) {
      stopPresence()
      return
    }
    startPresence(address)
  }, [address])

  // Tab/browser close: disconnect promptly so the backend marks the user offline.
  // Client-side route changes do not fire `pagehide`.
  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return
      socketRef.current?.disconnect()
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [])

  useEffect(() => {
    if (!address) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      return
    }

    if (deferLandSocket) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      return
    }

    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
    }

    const apiKey = process.env.REACT_APP_X_API_KEY || ''
    const socket = io(config.socketURL as string, {
      path: '/socket.io',
      transports: ['websocket'], // Prefer websocket only for better performance
      reconnection: true,
      // Never give up: this effect only re-runs on wallet change, so a manager that stopped retrying
      // (socket.io-client emits `reconnect_failed` and schedules nothing further) was dead for the session.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      extraHeaders: apiKey ? {
        'x-api-key': apiKey
      } : {}
    })

    socketRef.current = socket

    const markUserOnline = () => {
      if (socket.connected && address) {
        socket.emit('join-wallet-room', { walletAddress: address })
        socket.emit('user:online', { walletAddress: address })
      }
    }

    socket.on('connect', () => {
      markUserOnline()
    })

    socket.on('reconnect', () => {
      markUserOnline()
    })

    socket.on('disconnect', (reason) => {
      console.log('App socket disconnected:', reason)
    })

    // Listen for real-time notifications (alliance invites, messages, support requests, diplomacy, etc.)
    socket.on('notification:new', (payload: any) => {
      if (payload && address) {
        // Skip "user helped your building" notifications - user does not want these
        const title = (payload.title || '').toLowerCase()
        const message = (payload.message || '').toLowerCase()
        if (title.includes('helped') || message.includes('helped your') || message.includes('helped with')) {
          return
        }
        dispatch(addNotification({
          _id: payload._id || payload.id,
          id: payload._id || payload.id,
          walletAddress: payload.walletAddress || address,
          title: payload.title || '',
          message: payload.message || '',
          type: payload.type || 'system',
          isRead: false,
          createdAt: payload.createdAt,
          i18nKey: payload.i18nKey || null,
          i18nParams: payload.i18nParams || null,
          /** Carries the actionable payload (reinforce / rally / join-request) and the live-state
           *  marker. Dropping it here left a socket-delivered mail without its button until the
           *  next full refetch, and unable to resolve its own stale countdown. */
          metadata: payload.metadata || null,
        }))
      }
    })

    /** Auction sold (seller side): the bell notification arrives via `notification:new`; this
     *  event additionally refreshes the seller's GEM balance live (they received the sale
     *  proceeds) and pops a prominent toast so they immediately notice the sale. */
    socket.on('market:item-sold', (payload: any) => {
      if (!payload || !address) return
      // Pull the authoritative balance (proceeds were just credited server-side).
      dispatch(getResources(address, null, () => {}) as any)
      const name = payload.itemName || 'item'
      const proceeds = typeof payload.proceeds === 'number' ? payload.proceeds : null
      alert.open(
        proceeds != null
          ? `Your "${name}" sold! You received ${proceeds} GEM.`
          : `Your "${name}" sold!`,
      )
    })

    /** Alliance member donation received — pop an acknowledgment modal for the
     *  recipient and refresh their balances (resources were credited server-side;
     *  weapons list too when copies were transferred). Offline recipients see the
     *  persisted alliance notification instead. */
    socket.on('alliance:member-donation', (payload: any) => {
      if (!payload || !address) return
      dispatch(getResources(address, null, () => {}) as any)
      if (Array.isArray(payload.weapons) && payload.weapons.length > 0) {
        dispatch(fetchWeapons({ walletAddress: address }) as any)
      }
      const sender = payload.senderNick || 'An alliance member'
      alert.open(
        payload.message || `${sender} donated resources and/or weapons to you.`,
        { title: 'Donation Received' },
      )
    })

    /** MvM level milestones (castle unlocked / starter shield removed) — re-dispatched
     *  as a DOM event; LevelUpTracker turns it into the celebration modal. */
    socket.on('user:milestone', (payload: any) => {
      if (payload && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('user-milestone', { detail: payload }))
      }
    })

    /** Structured diplomacy gift delivery — diplomacy page shows same-modal UX as require-gift picker */
    socket.on('diplomacy:gift-received', (payload: any) => {
      if (payload && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('diplomacy-gift-received', { detail: payload }))
      }
    })

    /** Alliance join request declined (leader said no, or 10-min timeout) — show it to the requester. */
    socket.on('alliance:join-declined', (payload: any) => {
      const name = payload?.allianceName || 'the alliance'
      alert.open(
        payload?.timedOut
          ? `Your request to join ${name} expired — the leader didn’t respond in time.`
          : `Your request to join ${name} was declined.`,
      )
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('alliance-join-declined', { detail: payload }))
      }
    })

    /** Alliance join request approved — the requester is a member now; refresh whoever is listening. */
    socket.on('alliance:join-approved', (payload: any) => {
      const name = payload?.allianceName || 'the alliance'
      alert.open(`You were accepted into ${name}.`)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('alliance-join-approved', { detail: payload }))
      }
    })

    /** Post-victory castle conquer offer — surface to the hex battle victory modal. */
    socket.on('mvm:castle-conquer-offer', (payload: any) => {
      if (payload?.offerId && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('castle-conquer-offer', { detail: payload }))
      }
    })
    /** Defeated castle owner: the attacker's raid/conquer outcome — gates their defeat-modal loss. */
    socket.on('mvm:castle-defender-outcome', (payload: any) => {
      if (payload?.outcome && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('castle-defender-outcome', { detail: payload }))
      }
    })
    /** Leader approved the conquest — the winner's client shows a "conquered" result. */
    socket.on('alliance:conquer-approved', (payload: any) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('castle-conquer-result', { detail: { kind: 'approved', ...payload } }))
      }
    })
    /** Leader declined (or 10-min timeout) — re-show the victory modal with the kept resources. */
    socket.on('alliance:conquer-declined', (payload: any) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('castle-conquer-result', { detail: { kind: 'declined', ...payload } }))
      }
    })
    /** Recruiting turned out to be impossible (alliance full) — nothing happened, no resources. */
    socket.on('alliance:conquer-nullified', (payload: any) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('castle-conquer-result', { detail: { kind: 'nullified', ...payload } }))
      }
    })
    /** A new castle conquer request arrived for the leader — nudge the watcher to refresh. */
    socket.on('alliance:conquer-request', () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('alliance-conquer-request'))
      }
    })

    socket.on('notification:allDeleted', (payload: any) => {
      if (payload?.walletAddress === address) {
        dispatch(clearNotifications())
      }
    })

    const onLeadershipChallengeSync = () => {
      if (!address) return
      const now = Date.now()
      if (now - leadershipHudRefreshAtRef.current < 450) return
      leadershipHudRefreshAtRef.current = now
      const char =
        typeof global !== 'undefined' ? (String((global as { currentCharacterName?: string }).currentCharacterName || '')) : ''
      void getProfile(address, char)
    }
    socket.on(ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT, onLeadershipChallengeSync)

    // Mark user online immediately if already connected
    if (socket.connected) {
      markUserOnline()
    }

    return () => {
      socket.off('notification:new')
      socket.off('market:item-sold')
      socket.off('alliance:member-donation')
      socket.off('user:milestone')
      socket.off('diplomacy:gift-received')
      socket.off('alliance:join-declined')
      socket.off('alliance:join-approved')
      socket.off('mvm:castle-conquer-offer')
      socket.off('mvm:castle-defender-outcome')
      socket.off('alliance:conquer-approved')
      socket.off('alliance:conquer-declined')
      socket.off('alliance:conquer-nullified')
      socket.off('alliance:conquer-request')
      socket.off('notification:allDeleted')
      socket.off(ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT, onLeadershipChallengeSync)
      // Only disconnect if address changes (wallet disconnect)
      // Don't disconnect on component unmount to keep user online during navigation
      if (socketRef.current && !address) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [address, dispatch])

  return null // This component doesn't render anything
}

// Component to check blocked user status and redirect
const BlockedUserGuard: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { address } = useWeb3Context()
  const isBlocked = useSelector((state: RootState) => state.userModule.user.isBlocked)

  // Check blocked status when address is available or when isBlocked changes
  useEffect(() => {
    if (address && hasEstablishedGameSession() && isBlocked === true) {
      // Don't redirect from start page (it shows card instead)
      if (location.pathname === '/start') {
        return
      }
      // Force redirect to start page using window.location for direct URL access
      // This ensures redirect works even when typing URLs directly in browser
      // Start page will show banned card instead
      window.location.href = '/start'
    }
  }, [address, isBlocked, location.pathname])

  return null
}

// Component to handle initial routing based on wallet and nickname status
const InitialRouteGuard: React.FC = () => {
  const { address } = useWeb3Context()
  const location = useLocation()
  const navigate = useNavigate()
  const nickname = useSelector((state: RootState) => state.app.game.nickname)
  const userNickname = useSelector((state: RootState) => state.userModule.user.nickName)
  const profileWalletAddress = useSelector(
    (state: RootState) => state.userModule.user.walletAddress || '',
  )
  const currentNickname = nickname || userNickname || ''
  const isBlocked = useSelector((state: RootState) => state.userModule.user.isBlocked)
  const profileStaleWalletMismatch =
    !!address &&
    profileWalletAddress.length > 0 &&
    profileWalletAddress.toLowerCase() !== address.toLowerCase()

  // Redirect logic: guests land on / (connect page + email sign-in); /start is for nickname / enter flow only
  useEffect(() => {
    const path = location.pathname
    if (path === '/verify-email') return
    if (path.startsWith('/error/')) return

    const noIdentity = !address || !hasEstablishedGameSession()

    if (noIdentity) {
      if (path === '/start') {
        navigate('/', { replace: true })
        return
      }
      if (path !== '/') {
        navigate('/', { replace: true })
        return
      }
      return
    }

    const sessionDeadByTimer = hasSessionExpiresAt() && isSessionExpiredByTimestamp()

    // Expired access window: stay on connect (or leave /start) until refresh/sign-in — do not auto-enter /start
    // (otherwise bootstrap skips the session modal on `/` and the game can load without a valid session).
    if (hasEstablishedGameSession() && address && sessionDeadByTimer && path === '/start') {
      navigate('/', { replace: true })
      return
    }

    // Logged-in cold open: `/` is the connect layout root — send to start until they press Enter (see markGameRootEntryAllowed).
    if (hasEstablishedGameSession() && address && path === '/' && !isGameRootEntryAllowed() && !sessionDeadByTimer) {
      navigate('/start', { replace: true })
      return
    }

    // Has wallet or email session — need nickname before game (except while on /start)
    if (
      address &&
      !currentNickname &&
      !isBlocked &&
      path !== '/start' &&
      !profileStaleWalletMismatch &&
      !(path === '/' && isGameRootEntryAllowed())
    ) {
      if (sessionDeadByTimer) {
        navigate('/', { replace: true })
      } else {
        navigate('/start', { replace: true })
      }
      return
    }
  }, [address, currentNickname, location.pathname, navigate, isBlocked, profileStaleWalletMismatch])

  return null
}

const MainCharacterGuard: React.FC = () => {
  const { address } = useWeb3Context()
  const location = useLocation()
  const navigate = useNavigate()
  const [hasMainCharacter, setHasMainCharacter] = useState<boolean | null>(null)
  const nickname = useSelector((state: RootState) => state.app.game.nickname)
  const userNickname = useSelector((state: RootState) => state.userModule.user.nickName)
  const currentNickname = nickname || userNickname || ''

  const publicRoutes = ['/start', '/verify-email', '/error/404', '/error/500', '/error/network', '/select-main-character']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  useEffect(() => {
    const checkMainCharacter = async () => {
      if (!hasEstablishedGameSession() || !address || !currentNickname || isPublicRoute) {
        return
      }

      try {
        const { checkMainCharacterSelection } = await import('./common/kingdomApi')
        const response = await checkMainCharacterSelection(address)

        if (response.success) {
          setHasMainCharacter(response.hasMainCharacter)

          if (!response.hasMainCharacter && location.pathname !== '/select-main-character') {
            navigate('/select-main-character', { replace: true })
          }
        }
      } catch (err) {
        console.error('Error checking main character:', err)
      }
    }

    checkMainCharacter()
  }, [address, currentNickname, location.pathname, navigate, isPublicRoute])

  return null
}

const AppContent: React.FC<{ showAccount: boolean; setShowAccount: (flag: boolean) => void }> = ({ showAccount, setShowAccount }) => {
  const dispatch = useDispatch()
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)
  const { address, disconnect } = useWeb3Context()

  // Register session expiration handler (axios interceptor calls this on any 401)
  useEffect(() => {
    setSessionExpiredHandler(() => setShowSessionExpiredModal(true))
    return () => setSessionExpiredHandler(null)
  }, [])

  // Bootstrap: try silent refresh on load; if session is dead after pending reauth, leave deep links for connect.
  useEffect(() => {
    let cancelled = false

    const init = async () => {
      // After browser restart: localStorage holds virtual wallet + expiresAt; refresh cookies and expiry.
      // Also refresh if wallet persisted without expiry (edge cases / older builds).
      // After a 401 / explicit expiry we set pending reauth — do not silently refresh on reload or the
      // access window looks valid again, pending clears, and the user skips the session modal while
      // still needing to sign in again through the modal (or connect flow).
      if (
        !hasPendingReauthAfterSessionExpiry() &&
        (hasSessionExpiresAt() || hasPersistedEmailSessionWallet())
      ) {
        const recovered = await refreshAccessToken()
        if (cancelled) return
        if (recovered) {
          clearPendingReauthAfterSessionExpiry()
        }
      }

      const pathname = window.location.pathname
      const pathExempt =
        pathname === '/verify-email' || pathname.startsWith('/error/')
      const stillExpired = hasSessionExpiresAt() && isSessionExpiredByTimestamp()
      const mustLandOnConnect =
        hasPendingReauthAfterSessionExpiry() && stillExpired && pathname !== '/' && !pathExempt

      // After reload with a dead session, deep links must become connect + sign-in — not a frozen game route.
      if (mustLandOnConnect) {
        window.location.replace('/')
        return
      }
    }

    void init()

    return () => {
      cancelled = true
    }
  }, [])

  const handleSessionExpiredReturnToLogin = async () => {
    setShowSessionExpiredModal(false)
    try {
      await clearGameAuthSession()
    } catch {
      /* continue local cleanup */
    }
    clearSessionExpiresAt()
    clearPendingReauthAfterSessionExpiry()
    clearEclipseraSessionStorageKeys()
    setGameSessionWalletAddress(null)
    clearAuthToken()
    try {
      await disconnect()
    } catch {
      /* noop */
    }
    window.location.assign(`${window.location.origin}/`)
  }

  // Disable right-click context menu globally
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault()
      return false
    }

    document.addEventListener('contextmenu', handleContextMenu)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [])
  const playClickSound = useClickSound()
  const isBlocked = useSelector((state: RootState) => state.userModule.user.isBlocked)
  const [isCheckingBlocked, setIsCheckingBlocked] = useState(false)

  // Enable comprehensive devtools blocking
  // useDevToolsBlocker()

  // Enable network error handling
  // useNetworkErrorHandler()

  // Load settings from localStorage on mount
  useEffect(() => {
    dispatch(loadSettings())
  }, [dispatch])

  // Initialize referral code from URL on app mount
  useEffect(() => {
    initializeReferralCode()
  }, [])

  // Fetch user profile on mount if connected to check blocked status early
  // This ensures blocked status is checked before rendering routes when typing URLs directly
  useEffect(() => {
    let ignore = false
    const checkUserBlockedStatus = async () => {
      if (hasEstablishedGameSession() && address) {
        // Check if we already know the blocked status
        // If not, fetch profile to get latest blocked status
        if (isBlocked === undefined || isBlocked === false) {
          if (ignore) return
          setIsCheckingBlocked(true)
          try {
            // Fetch profile to get latest blocked status
            const characterName = (global as any).currentCharacterName || ''
            await getProfile(address, characterName)
          } catch (error) {
            console.error('Error fetching profile for blocked check:', error)
          } finally {
            if (!ignore) setIsCheckingBlocked(false)
          }
        }
      }
    }

    checkUserBlockedStatus()

    return () => {
      ignore = true
    }
  }, [address, isBlocked])

  const openModal = (flag: boolean) => {
    setShowAccount(flag)
  }

  // Optimized global click sound handler with event delegation and throttling
  useEffect(() => {
    let lastPlayTime = 0
    const THROTTLE_MS = 50 // Minimum time between sounds (50ms)

    const handleClick = (e: MouseEvent) => {
      // Skip clicks on disabled elements or if sound is disabled
      const target = e.target as HTMLElement
      if (target?.closest('[disabled]') || target?.closest('.no-click-sound')) {
        return
      }

      const now = Date.now()
      if (now - lastPlayTime < THROTTLE_MS) {
        return
      }

      lastPlayTime = now
      playClickSound()
    }

    // Use capture phase for better performance with event delegation
    document.addEventListener('click', handleClick, true)

    return () => {
      document.removeEventListener('click', handleClick, true)
    }
  }, [playClickSound])

  // If user is blocked and wallet is connected, redirect all routes to start page
  // Start page will show banned card instead
  // This ensures all pages (market, map, inventory, pvp, etc.) redirect to start page
  if (hasEstablishedGameSession() && address && isBlocked === true) {
    return (
      <BrowserRouter>
        <VercelAssetBootstrap>
          <SessionExpiredModal open={showSessionExpiredModal} onRefresh={handleSessionExpiredReturnToLogin} />
          <MissionObjectiveCompleteNotification />
          <MissionQuestCompleteNotification />
          <MissionCompleteNotification />
          <Routes>
            {/* Allow start page to render (it will show banned card) */}
            <Route
              path="/start"
              element={
                <Suspense fallback={<FireLoading />}>
                  <StartPage />
                </Suspense>
              }
            />
            {/* All other routes (market, map, inventory, pvp, etc.) redirect to start page */}
            <Route
              path="*"
              element={
                <Navigate to="/start" replace />
              }
            />
          </Routes>
        </VercelAssetBootstrap>
      </BrowserRouter>
    )
  }

  // Show loading while checking blocked status (only if on a non-start route)
  if (isCheckingBlocked && hasEstablishedGameSession() && address && window.location.pathname !== '/start') {
    return <FireLoading />
  }

  return (
    <BrowserRouter>
      <VercelAssetBootstrap>
        <DayNightProvider>
        <FogTransitionProvider >
          <SessionExpiredModal open={showSessionExpiredModal} onRefresh={handleSessionExpiredReturnToLogin} />
          <KingdomMusicPlayer />
          <PvPMusicPlayer />
          <StartMusicPlayer />
          <MiniGameMusicPlayer />
          <GlobalAttackAlert />
          <MissionObjectiveCompleteNotification />
          <MissionQuestCompleteNotification />
          <MissionCompleteNotification />
          <GlobalNotificationToastHost />
          {/* Styles every `title` in the app; see GlobalTooltipHost for why it works by delegation. */}
          <GlobalTooltipHost />
          {/* Every 5–10 min: nudge level-5+ players without an alliance toward the alliance page (3-day snooze). */}
          <AllianceJoinNudgeHost />
          <LevelUpTracker />
          <UserOnlineStatusManager />
          <RouteChangeListener />
          {/* Always-available exit from the day-1 guide (renders itself only while it runs). */}
          <FtueSkipButton />
          <BlockedUserGuard />
          <InitialRouteGuard />
          <MainCharacterGuard />
          <AddEmailPromptBanner />
          <AutoLinkVirtualSolanaWallet />
          <GlobalPvPRematchHost />
          <GlobalDirectChallengeHost />
          <GlobalFriendRequestHost />
          <AllianceLeadershipChallengeGlobalHost />
          <AlertHost />
          <DepositAlertHost />
          <EcsPackAlertHost />
          <CursorClickEffect />
          {/* Resource-icon claim burst (flies to the top-bar counter) — see ResourceClaimFlyLayer */}
          <ResourceClaimFlyLayer />
          {/* Fires the burst on ANY resource gain (missions, quests, gifts…) — see ResourceGainWatcher */}
          <ResourceGainWatcher />
          {/* Resource consumption burst (erupts OUT of the counter on spend) — see ResourceSpendFlyLayer */}
          <ResourceSpendFlyLayer />
          {/* Fires the consumption burst on ANY resource decrease (research, upgrades, purchases…) */}
          <ResourceSpendWatcher />
          {/* Alliance weekly task vote gating: member vote modal / leader forced result modal */}
          <AllianceTaskWatcher />
          <AllianceJoinRequestWatcher />
          <AllianceConquerRequestWatcher />
          <CastleConquerResultWatcher />
          {/* Keep the land page alive across navigations — see PersistentLandPage */}
          <PersistentLandPage showAccount={showAccount} openModal={openModal} />
          {/* Destinations outside the cold-boot gate load their artwork behind the game loading
              screen before mounting — see RouteAssetGate. No-op once an area is warm. */}
          <RouteAssetGateByPath>
          <Routes>
            <Route
              path="/start"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <StartPage />
                </Suspense>
              }
            />
            <Route
              path="/verify-email"
              element={
                <Suspense fallback={<FireLoading />}>
                  <VerifyEmailPage />
                </Suspense>
              }
            />
            <Route
              path="/select-default-character"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <DefaultCharacterSelectPage />
                </Suspense>
              }
            />
            <Route
              path="/select-main-character"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <MainCharacterSelectPage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <KingdomMainPage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/general-appointment"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <GeneralAppointmentPage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/general-visit"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <GeneralVisitPage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/retirement"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <RetirementPage />
                </Suspense>
              }
            />
            {/* "/" is handled by <PersistentLandPage> above — renders null here
                so React Router still matches the route (blocking the "*" catch-all)
                while PersistentLandPage is the actual visible content. */}
            <Route path="/" element={null} />
            <Route path="/market" element={<Navigate to="/shop/marketplace" replace />} />
            <Route
              path="/shop/:tab"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <ShopPage onModalShow={openModal} />
                </Suspense>
              }
            />
            <Route
              path="/shop"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <ShopPage onModalShow={openModal} />
                </Suspense>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <LeaderboardPage />
                </Suspense>
              }
            />
            <Route
              path="/map"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <MapPage />
                </Suspense>
              }
            />
            <Route
              path="/castle1"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <Castle1
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/castle2"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <Castle2
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/castle3"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <Castle3
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/castle4"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <Castle4
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/castle5"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <Castle5
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/mining-factory"
              element={
                <Suspense
                  fallback={<MiningLoading />}
                >
                  <MiningFactory
                    showAccount={showAccount}
                    setShowAccount={setShowAccount}
                  />
                </Suspense>
              }
            />
            <Route
              path="/eclipsera-bonds"
              element={
                <Suspense fallback={<FireLoading />}>
                  <MiniGamePage />
                </Suspense>
              }
            />

            <Route
              path="/pve/character-select/:roundNumber"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <CharacterSelectPage />
                </Suspense>
              }
            />

            <Route
              path="/characters"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <CharacterDetailsPage />
                </Suspense >
              }
            />
            {/* ── Routes below exist only in one place (duplicates removed) ── */}
            <Route
              path="/kingdom/diplomacy"
              element={
                <Suspense fallback={<FireLoading />}>
                  <DiplomacyPage />
                </Suspense>
              }
            />
            {/* Alliance: canonical /alliance/* ; /kingdom/alliance/* kept for old links */}
            <Route
              path="/alliance"
              element={
                <Suspense fallback={<FireLoading />}>
                  <AlliancePage />
                </Suspense>
              }
            />
            <Route
              path="/alliance/manage"
              element={
                <Suspense fallback={<FireLoading />}>
                  <AllianceManagePage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/alliance"
              element={
                <Suspense fallback={<FireLoading />}>
                  <AlliancePage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/alliance/manage"
              element={
                <Suspense fallback={<FireLoading />}>
                  <AllianceManagePage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/research"
              element={
                <Suspense fallback={<FireLoading />}>
                  <TechnologyResearchPage />
                </Suspense>
              }
            />
            <Route
              path="/kingdom/mvm-captives"
              element={
                <Suspense fallback={<FireLoading />}>
                  <KingdomMvmCaptivesPage />
                </Suspense>
              }
            />
            <Route
              path="/hex-battle"
              element={
                <Suspense fallback={<FireLoading />}>
                  <HexBattlePage />
                </Suspense>
              }
            />
            <Route
              path="/world-map"
              element={
                <Suspense fallback={<WorldMapRouteFallback />}>
                  <WorldMapPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <InventoryPage />
                </Suspense>
              }
            />


            <Route
              path="/pve/rounds/:area"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <PVERound />
                </Suspense>
              }
            />

            <Route
              path="/pve/battle-preview/:characterIdAndRound"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <BattlePreviewPage />
                </Suspense>
              }
            />

            <Route
              path="/pve/battlefield/:characterIdAndRound"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <BattlefieldPage />
                </Suspense>
              }
            />
            <Route
              path="/gem-pack"
              element={<Navigate to="/shop/gem-pack" replace />}
            />
            <Route
              path="/bank"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <BankPage />
                </Suspense>
              }
            />
            <Route
              path="/support"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <SupportPage />
                </Suspense>
              }
            />
            <Route
              path="/espionage"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <EspionagePageV2 />
                </Suspense>
              }
            />
            <Route
              path="/espionage-v2"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <EspionagePageV2 />
                </Suspense>
              }
            />
            <Route
              path="/espionage-legacy"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <EspionagePage />
                </Suspense>
              }
            />
            <Route
              path="/counter-espionage"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <EspionagePage />
                </Suspense>
              }
            />
            <Route
              path="/spy-splash"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <SpySplashPage />
                </Suspense>
              }
            />
            <Route
              path="/tower"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <TowerPage />
                </Suspense>
              }
            />
            <Route
              path="/hospital"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <HospitalPage />
                </Suspense>
              }
            />
            <Route
              path="/woodhouse"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <WoodHousePage />
                </Suspense>
              }
            />
            <Route
              path="/wheatfarm"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <WheatFarmPage />
                </Suspense>
              }
            />
            <Route
              path="/stonefactory"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <StoneFactoryPage />
                </Suspense>
              }
            />
            <Route
              path="/training"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <TrainingPage />
                </Suspense>
              }
            />
            <Route
              path="/tournament"
              element={
                <Suspense fallback={<FireLoading />}>
                  <TournamentPage />
                </Suspense>
              }
            />
            <Route
              path="/tournament/battle"
              element={
                <Suspense fallback={<FireLoading />}>
                  <TournamentBattlePage />
                </Suspense>
              }
            />
            <Route
              path="/hunting-lodge"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <HuntingLodgePage />
                </Suspense>
              }
            />
            <Route
              path="/blacksmith"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <BlacksmithPage />
                </Suspense>
              }
            />
            <Route
              path="/iron-mine"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <IronMinePage />
                </Suspense>
              }
            />
            <Route
              path="/bakery"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <BakeryPage />
                </Suspense>
              }
            />
            <Route
              path="/well"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <WellPage />
                </Suspense>
              }
            />
            <Route
              path="/wall"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <WallPage />
                </Suspense>
              }
            />
            <Route
              path="/pvp/play"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <PvPPage />
                </Suspense>
              }
            />
            <Route
              path="/pvp/direct-challenge/accepted"
              element={
                <Suspense fallback={<FireLoading />}>
                  <PvPDirectChallengeAcceptedPage />
                </Suspense>
              }
            />
            <Route
              path="/pvp/direct-challenge"
              element={
                <Suspense fallback={<FireLoading />}>
                  <PvPDirectChallengePage />
                </Suspense>
              }
            />
            <Route
              path="/pvp"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <PvpLobbyPage />
                </Suspense>
              }
            />
            {/* Direct card-c lick flow: no room created yet, no back-end on load or back */}
            <Route
              path="/pvp/select-characters"
              element={
                <Suspense fallback={<FireLoading />}>
                  <PvPCharacterSelectSimplePage />
                </Suspense>
              }
            />
            {/* Direct card-click flow: creates room + submits characters here, then polls */}
            <Route
              path="/pvp/waiting"
              element={
                <Suspense fallback={<FireLoading />}>
                  <PvPWaitingSimplePage />
                </Suspense>
              }
            />
          <Route
            path="/pvp/battle/:battleId/observe"
            element={
              <Suspense
                fallback={
                  <FireLoading />
                }
              >
                <PvPObserverBattlePage />
              </Suspense>
            }
          />
            <Route
              path="/pvp/alliance-leadership/:roomId"
              element={
                <Suspense fallback={<FireLoading />}>
                  <PvPAllianceLeadershipRoomPage />
                </Suspense>
              }
            />
            <Route
              path="/pvp/battle/:battleId"
              element={
                <Suspense
                  fallback={
                    <FireLoading />
                  }
                >
                  <PvPBattlePage />
                </Suspense>
              }
            />
            {/* Error Pages */}
            <Route
              path="/error/404"
              element={
                <Suspense fallback={<FireLoading />}>
                  <NotFoundPage />
                </Suspense>
              }
            />
            <Route
              path="/error/500"
              element={
                <Suspense fallback={<FireLoading />}>
                  <ServerErrorPage />
                </Suspense>
              }
            />
            <Route
              path="/error/network"
              element={
                <Suspense fallback={<FireLoading />}>
                  <NetworkErrorPage />
                </Suspense>
              }
            />
            {/* Catch all - redirect to 404 */}
            <Route path="*" element={<Navigate to="/error/404" replace />} />
          </Routes>
          </RouteAssetGateByPath>
        </FogTransitionProvider>
        </DayNightProvider>
      </VercelAssetBootstrap>
    </BrowserRouter >
  )
}

const App: React.FC = () => {
  const [showAccount, setShowAccount] = useState(false)

  return (
    <AppViewportProvider>
      <Suspense fallback={<FireLoading />}>
        <Providers>
          <Provider store={store}>
            <AppContent showAccount={showAccount} setShowAccount={setShowAccount} />
          </Provider>
        </Providers>
      </Suspense>
    </AppViewportProvider>
  )
}

export default App
